// src/services/invoiceService.js
import apiClient from "./apiClient";
import {
  getOrganizationId,
  getOrganizationName,
  fetchOrganizationName,
} from "../utils/organizationUtils";
import {
  formatStockInvoiceSupplierFields,
  normalizeInvoiceListResponse,
} from "../utils/nameFormatUtils";
import {
  getInvoiceAttachment,
  rememberInvoiceAttachment,
  unwrapInvoiceRecord,
  resolveAttachmentUrl,
  readInvoiceAttachmentMemory,
  mergeInvoicesPreservingAttachments,
} from "../utils/invoiceAttachmentUtils";

const API_URL = `/invoices`; // Use relative path since apiClient has baseURL

// getInvoicesByPatientId is called every time the timeline's print menu opens
// (PatientTimelinePrintMenu) — invoices already saved don't change, so cache
// per patient and only refetch after a short TTL or an explicit invalidation
// (called from createInvoice below on a successful save).
const INVOICES_CACHE_TTL_MS = 5 * 60 * 1000;
let __invoicesByPatientCache = new Map(); // patientId -> { data, timestamp, inFlight }

export const invalidateInvoicesCache = (patientId = null) => {
  if (patientId == null) {
    __invoicesByPatientCache = new Map();
    return;
  }
  __invoicesByPatientCache.delete(String(patientId));
};

const STOCK_INVOICE_TYPES = new Set(["HOMEOPATHY", "ALLOPATHY", "PATHOLOGY"]);

/** Relation FK keys that must land in org hospital invoice table columns. */
const INVOICE_RELATION_ID_FIELDS = [
  { camel: "patientId", snake: "patient_id", aliases: ["patient"] },
  {
    camel: "appointmentId",
    snake: "appointment_id",
    aliases: ["appointment"],
  },
  {
    camel: "doctorId",
    snake: "doctor_id",
    aliases: ["doctor", "consultingDoctorId", "consulting_doctor_id"],
  },
  { camel: "supplierId", snake: "supplier_id", aliases: ["supplier"] },
  {
    camel: "departmentId",
    snake: "department_id",
    aliases: ["department"],
  },
  { camel: "admissionId", snake: "admission_id", aliases: ["admission"] },
  {
    camel: "prescriptionId",
    snake: "prescription_id",
    aliases: ["prescription", "opdPrescriptionId", "opd_prescription_id", "ipdPrescriptionId", "ipd_prescription_id"],
  },
  {
    camel: "userId",
    snake: "user_id",
    aliases: ["createdBy", "created_by"],
  },
  {
    camel: "organizationId",
    snake: "organization_id",
    aliases: ["organization"],
  },
  {
    camel: "creditOrganizationId",
    snake: "credit_organization_id",
    aliases: [],
  },
];

const hasInvoiceText = (value) => {
  if (value == null || value === "") return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && normalized !== "n/a" && normalized !== "na";
};

/**
 * Coerce a relation id from primitives or nested `{ id }` objects.
 * Returns a positive integer, or null when missing/invalid.
 */
const toPositiveRelationId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if (Array.isArray(value)) return null;
    return toPositiveRelationId(
      value.id ??
        value.patientId ??
        value.doctorId ??
        value.appointmentId ??
        value.supplierId ??
        value.admissionId ??
        value.departmentId ??
        value.prescriptionId ??
        value.prescription_id
    );
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

/**
 * Ensure patient_id / appointment_id / doctor_id / supplier_id / prescription_id
 * (and related FKs) are present as both camelCase and snake_case numbers so org
 * invoice inserts succeed whether the backend DTO uses Jackson camelCase or
 * snake_case columns.
 */
export const normalizeInvoiceRelationIds = (invoice = {}) => {
  if (!invoice || typeof invoice !== "object" || Array.isArray(invoice)) {
    return invoice;
  }

  const payload = { ...invoice };

  for (const field of INVOICE_RELATION_ID_FIELDS) {
    const keys = [field.camel, field.snake, ...(field.aliases || [])];
    let resolved = null;
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
      resolved = toPositiveRelationId(payload[key]);
      if (resolved != null) break;
    }
    // Also accept nested objects when only alias key exists with object value
    if (resolved == null) {
      for (const key of field.aliases || []) {
        resolved = toPositiveRelationId(payload[key]);
        if (resolved != null) break;
      }
    }
    if (resolved == null) continue;
    payload[field.camel] = resolved;
    payload[field.snake] = resolved;
  }

  return payload;
};

const resolvePayloadAttachment = (invoice = {}) =>
  getInvoiceAttachment(invoice) || resolveAttachmentUrl(invoice.attachment) || "";

/**
 * List APIs often omit attachment; keep the URL sent on create/update so InvoiceTable can show it.
 * Also fill particulars on the returned object when the API response drops them.
 */
const enrichInvoiceMutationResult = (requestInvoice = {}, responseInvoice) => {
  const unwrapped = unwrapInvoiceRecord(responseInvoice);
  const result =
    unwrapped && typeof unwrapped === "object" && !Array.isArray(unwrapped)
      ? { ...unwrapped }
      : unwrapped;

  if (!result || typeof result !== "object" || Array.isArray(result)) {
    // Still remember from request if we at least know the new id somehow
    return responseInvoice;
  }

  const invoiceId = result.id ?? result.invoiceId ?? requestInvoice.id;
  const attachmentUrl =
    resolvePayloadAttachment(result) || resolvePayloadAttachment(requestInvoice) || "";
  const particulars =
    result.particulars ??
    result.comments ??
    result.remarks ??
    requestInvoice.particulars ??
    requestInvoice.comments ??
    requestInvoice.remarks ??
    null;

  if (attachmentUrl) {
    const orgId =
      getOrganizationId(null) ||
      result.organizationId ||
      result.organization_id ||
      requestInvoice.organizationId ||
      requestInvoice.organization_id ||
      null;
    if (invoiceId != null && invoiceId !== "") {
      rememberInvoiceAttachment(orgId, invoiceId, attachmentUrl);
    }
    if (!getInvoiceAttachment(result)) {
      result.attachment = attachmentUrl;
    }
  }

  if (
    (result.particulars == null || result.particulars === "") &&
    particulars != null &&
    particulars !== ""
  ) {
    result.particulars = particulars;
  }

  // Keep relation FKs when API create/update response omits them
  for (const field of INVOICE_RELATION_ID_FIELDS) {
    const resultId = toPositiveRelationId(
      result[field.camel] ?? result[field.snake]
    );
    if (resultId != null) {
      result[field.camel] = resultId;
      continue;
    }
    const requestId = toPositiveRelationId(
      requestInvoice[field.camel] ?? requestInvoice[field.snake]
    );
    if (requestId != null) {
      result[field.camel] = requestId;
      result[field.snake] = requestId;
    }
  }

  // Preserve top-level id for callers that check invoice.id after create
  if (result.id == null && result.invoiceId != null) {
    result.id = result.invoiceId;
  }

  return result;
};

/** Resolve hospital name for income invoices when fromName is missing. */
const resolveHospitalInvoiceFromName = async (invoice = {}, preferredName = null) => {
  if (hasInvoiceText(preferredName)) return String(preferredName).trim();
  if (hasInvoiceText(invoice.fromName)) return String(invoice.fromName).trim();
  if (hasInvoiceText(invoice.from_name)) return String(invoice.from_name).trim();

  const fromUser = getOrganizationName(null);
  if (hasInvoiceText(fromUser)) return String(fromUser).trim();

  const orgId =
    getOrganizationId(null) ||
    invoice.organizationId ||
    invoice.organization_id ||
    null;

  if (orgId) {
    try {
      const fetched = await fetchOrganizationName(Number(orgId));
      if (hasInvoiceText(fetched)) return String(fetched).trim();
    } catch {
      // Non-blocking — fall through to default
    }
  }

  return "Aryan Hospital";
};

/**
 * Resolve logged-in user id for invoice audit (user_id column).
 */
const resolveLoggedInUserId = () => {
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      const id = toPositiveRelationId(parsed?.id ?? parsed?.userId ?? parsed?.user_id);
      if (id != null) return id;
    }
  } catch {
    // ignore parse errors
  }
  return null;
};

/**
 * Ensure user_id is always present on create/update payloads.
 * Prefer an explicit payload value; otherwise use the logged-in user.
 */
const ensureInvoiceUserId = (invoice = {}) => {
  const payload = { ...invoice };
  const existing = toPositiveRelationId(
    payload.userId ?? payload.user_id ?? payload.createdBy ?? payload.created_by
  );
  const userId = existing ?? resolveLoggedInUserId();
  if (userId != null) {
    payload.userId = userId;
    payload.user_id = userId;
  }
  return payload;
};

/**
 * Fail fast when create response has no id (org table insert must return generated key).
 */
const assertInvoiceCreated = (data) => {
  const unwrapped = unwrapInvoiceRecord(data);
  const id = toPositiveRelationId(
    unwrapped?.id ?? unwrapped?.invoiceId ?? unwrapped?.invoice_id
  );
  if (!id) {
    throw new Error(
      "Invoice creation did not return a valid invoice ID. Please try again."
    );
  }
  if (unwrapped && typeof unwrapped === "object" && !Array.isArray(unwrapped)) {
    unwrapped.id = id;
  }
  return unwrapped;
};

/**
 * Ensure income invoices persist fromName + hospital organizationId like other billing flows.
 * Table routing uses query ?organizationId=hospitalOrg (apiClient). Body fields:
 * - CASH: organizationId = hospital org
 * - CREDIT: organizationId = credit org, hospitalOrganizationId = hospital org
 */
const enrichInvoiceCreatePayload = async (invoice = {}) => {
  let payload = normalizeInvoiceRelationIds(ensureInvoiceUserId({ ...invoice }));
  const invoiceType = String(
    payload.invoiceType || payload.invoice_type || ""
  ).toUpperCase();

  // Persist enum-style invoice types (HOMEOPATHY, ALLOPATHY, PATHOLOGY, etc.)
  if (invoiceType) {
    payload.invoiceType = invoiceType;
  }

  const hospitalOrgId =
    toPositiveRelationId(
      payload.hospitalOrganizationId ?? payload.hospital_organization_id
    ) ?? getOrganizationId(null);

  const paymentMode = String(
    payload.paymentMode || payload.payment_mode || "CASH"
  ).toUpperCase();

  const creditOrgId = toPositiveRelationId(
    payload.creditOrganizationId ?? payload.credit_organization_id
  );

  if (paymentMode === "CREDIT" && creditOrgId) {
    payload.organizationId = creditOrgId;
    payload.organization_id = creditOrgId;
    payload.creditOrganizationId = creditOrgId;
    payload.credit_organization_id = creditOrgId;
    if (hospitalOrgId) {
      payload.hospitalOrganizationId = hospitalOrgId;
      payload.hospital_organization_id = hospitalOrgId;
    }
  } else if (hospitalOrgId) {
    const rowOrgId = toPositiveRelationId(
      payload.organizationId ?? payload.organization_id
    );
    if (!rowOrgId) {
      payload.organizationId = hospitalOrgId;
      payload.organization_id = hospitalOrgId;
    }
  }

  // Always persist logged-in user on create
  payload = ensureInvoiceUserId(payload);

  // Re-normalize after org/user fill so snake_case FKs are set
  payload = normalizeInvoiceRelationIds(payload);

  // Normalize optional attachment to a plain string URL/path for the backend + table memory
  const attachmentUrl = resolvePayloadAttachment(payload);
  if (attachmentUrl) {
    payload.attachment = attachmentUrl;
  } else {
    delete payload.attachment;
  }

  if (!STOCK_INVOICE_TYPES.has(invoiceType)) {
    if (!hasInvoiceText(payload.fromName) && !hasInvoiceText(payload.from_name)) {
      payload.fromName = await resolveHospitalInvoiceFromName(payload);
    } else if (!hasInvoiceText(payload.fromName) && hasInvoiceText(payload.from_name)) {
      payload.fromName = String(payload.from_name).trim();
    }
  }

  return payload;
};

export const createInvoice = async (invoice) => {
  try {
    const enriched = await enrichInvoiceCreatePayload(invoice);
    const payload = formatStockInvoiceSupplierFields(enriched);
    const parsedOrgId = Number(payload?.organizationId ?? payload?.organization_id);
    const requestConfig =
      Number.isFinite(parsedOrgId) && parsedOrgId > 0
        ? { params: { organizationId: parsedOrgId } }
        : undefined;
    // Keep an explicit hospital organizationId on the query string so patient
    // portal bookings are not rewritten to the logged-in user's default org.
    console.log(`[invoiceService] Creating invoice with data:`, payload);
    const response = await apiClient.post(API_URL, payload, requestConfig);
    console.log(`[invoiceService] Invoice created successfully:`, response.data);
    const created = assertInvoiceCreated(
      normalizeInvoiceListResponse(response.data)
    );
    // A freshly-created invoice must show up next time the timeline print
    // menu opens — invalidate by patientId when known, else clear everything
    // (donation/misc invoices key off a party, not a patientId).
    const invoicePatientId = payload?.patientId ?? payload?.patient_id ?? created?.patientId;
    invalidateInvoicesCache(invoicePatientId ?? null);
    return enrichInvoiceMutationResult(payload, created);
  } catch (error) {
    console.error("[invoiceService] Error creating invoice:", error);
    console.error("[invoiceService] Error response:", error.response?.data);
    console.error("[invoiceService] Error status:", error.response?.status);
    
    // Provide more helpful error message
    if (error.response?.status === 500) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Internal server error";
      console.error(`[invoiceService] Backend error (500): ${errorMessage}`);
      
      // Check if it's a table-related error
      if (errorMessage.includes("table") || errorMessage.includes("Failed to") || errorMessage.includes("does not exist")) {
        throw new Error(`Invoice table not found or not accessible. Please ensure the organization-specific invoice table ({organizationId}_invoice) exists. Use the Organization Utilities page to create missing tables. Original error: ${errorMessage}`);
      }
      
      throw new Error(`Failed to create invoice: ${errorMessage}`);
    }
    
    throw error;
  }
};

export const getInvoiceList = async (options = {}) => {
  try {
    const { fromDate, toDate, page, pageSize, fullRange } = options;
    const params = {};

    if (fullRange) {
      params.fullRange = true;
    } else {
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
    }
    if (page != null) params.page = page;
    if (pageSize != null) params.pageSize = pageSize;

    const response = await apiClient.get(API_URL, {
      params: Object.keys(params).length ? params : undefined,
    });
    const normalized = normalizeInvoiceListResponse(response.data);
    const orgId = getOrganizationId(null);
    const attachmentMemory = readInvoiceAttachmentMemory(orgId);

    if (Array.isArray(normalized)) {
      return mergeInvoicesPreservingAttachments(normalized, [], attachmentMemory);
    }

    if (normalized && typeof normalized === "object") {
      const [merged] = mergeInvoicesPreservingAttachments(
        [normalized],
        [],
        attachmentMemory
      );
      return merged || normalized;
    }

    return normalized;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

export const updateInvoice = async (id, invoice) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    let payload = normalizeInvoiceRelationIds(ensureInvoiceUserId({ ...invoice }));
    if (payload.invoiceType || payload.invoice_type) {
      payload.invoiceType = String(
        payload.invoiceType || payload.invoice_type || ""
      ).toUpperCase();
    }
    const attachmentUrl = resolvePayloadAttachment(payload);
    if (attachmentUrl) {
      payload.attachment = attachmentUrl;
    }
    // Always persist logged-in user on update when missing
    payload = ensureInvoiceUserId(payload);
    payload = normalizeInvoiceRelationIds(payload);
    const formattedPayload = formatStockInvoiceSupplierFields(payload);
    const response = await apiClient.put(`${API_URL}/${id}`, formattedPayload);
    return enrichInvoiceMutationResult(
      { ...formattedPayload, id },
      normalizeInvoiceListResponse(response.data)
    );
  } catch (error) {
    console.error("Error updating invoice:", error);
    throw error;
  } 
};

export const getInvoiceById = async (id) => {
  try {
    const response = await apiClient.get(`${API_URL}/${id}`);
    return normalizeInvoiceListResponse(response.data);
  } catch (error) {
    console.error("Error fetching invoice by id:", error);
    throw error;
  }
};

export const getInvoicesByPatientId = async (patientId, options = {}) => {
  const { forceRefresh = false } = options;
  const key = String(patientId);
  const now = Date.now();

  if (forceRefresh) {
    invalidateInvoicesCache(patientId);
  } else {
    const cached = __invoicesByPatientCache.get(key);
    if (cached?.data && now - cached.timestamp < INVOICES_CACHE_TTL_MS) {
      return cached.data;
    }
    if (cached?.inFlight) {
      return cached.inFlight;
    }
  }

  const request = apiClient
    .get(`${API_URL}/patient/${patientId}`)
    .then((response) => {
      __invoicesByPatientCache.set(key, { data: response.data, timestamp: Date.now(), inFlight: null });
      return response.data;
    })
    .catch((error) => {
      console.error("Error fetching prescriptions by patient:", error);
      __invoicesByPatientCache.delete(key);
      throw error;
    });

  __invoicesByPatientCache.set(key, { data: null, timestamp: 0, inFlight: request });
  return request;
};


export default { getInvoicesByPatientId }